/* mbe-dayplans-autogen.js (v6)
   Universal auto day-plan generator (works across ALL destinations / markings)

   Upgrades included:
   1) Region clustering (DBSCAN-ish) -> plans come from a real "area" instead of random mixing
   2) Optional Mapbox route validation (async, cached) -> fixes "walk in mountains / zigzag" by verifying real roads/paths
   3) Smarter theme/title generator (varied, category-driven, not “AI”, but feels curated)

   Guarantees:
   - Never suggests Parking
   - Plans always generated (fallback path, avoids over-aggressive nulls)
   - transport_mode is NOT forced to walk; auto picks walk/drive per plan
   - Summary opener/closer is neutral (works for walk or drive)
*/
(function () {
  if (window.MBE_AUTO_DAYPLANS) return;
  window.MBE_AUTO_DAYPLANS = true;

  window.__MBE_AUTOGUIDE_ACTIVE_KEY__ = window.__MBE_AUTOGUIDE_ACTIVE_KEY__ || "";

  // ---------- cache ----------
  window.__MBE_AUTOGUIDE_CACHE__ = window.__MBE_AUTOGUIDE_CACHE__ || {};
  function mbeDsKey_(ds) {
    // 1) best: current places filename/url if your loader sets it
    const fromWindow =
        window.MAPSBYEMA_ACTIVE_PLACES_FILENAME ||
        window.MAPSBYEMA_ACTIVE_PLACES_URL ||
        window.MAPSBYEMA_ACTIVE_DATASET_KEY;

    // 2) next best: ds hints
    const fromDs = ds && (ds.filename || ds.url || ds.key || ds.title);

    // 3) last resort: page path (prevents "unknown" collisions)
    const base = String(fromWindow || fromDs || location.pathname || "unknown").trim();

    return base.toLowerCase();
    }

  function mbeCacheKey_(ds) { return "mbe_autoguide_v6:" + mbeDsKey_(ds); }
  function mbeLoadCache_(ds) {
    const k = mbeCacheKey_(ds);
    if (window.__MBE_AUTOGUIDE_CACHE__[k]) return window.__MBE_AUTOGUIDE_CACHE__[k];
    try {
      const raw = localStorage.getItem(k);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && Array.isArray(obj.plans) && obj.plans.length) {
          window.__MBE_AUTOGUIDE_CACHE__[k] = obj;
          return obj;
        }
      }
    } catch (e) {}
    return null;
  }
  function mbeSaveCache_(ds, dest) {
    const k = mbeCacheKey_(ds);
    window.__MBE_AUTOGUIDE_CACHE__[k] = dest;
    try { localStorage.setItem(k, JSON.stringify(dest)); } catch (e) {}
  }

  // Separate cache for async Mapbox validation results
  function mbeRouteCacheKey_(ds) { return "mbe_autoguide_routes_v6:" + mbeDsKey_(ds); }
  function mbeLoadRouteCache_(ds) {
    try {
      const raw = localStorage.getItem(mbeRouteCacheKey_(ds));
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function mbeSaveRouteCache_(ds, obj) {
    try { localStorage.setItem(mbeRouteCacheKey_(ds), JSON.stringify(obj)); } catch (e) {}
  }

  // ---------- utils ----------
  function n(x) { x = Number(x); return isFinite(x) ? x : 0; }
  function s(x) { return String(x == null ? "" : x); }
  function slugify(x) {
    return s(x).trim().toLowerCase()
      .replace(/['"]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  // deterministic PRNG
  function mbeHash32_(str) {
    str = String(str || "");
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
    }
    return h >>> 0;
  }
  function mulberry32(seed) {
    let a = seed >>> 0;
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  let __rand = Math.random;
  function pick(arr) { return arr[Math.floor(__rand() * arr.length)]; }
  function shuffle(a) {
    a = a.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(__rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function hasCoords(p) { return p && isFinite(n(p.lat)) && isFinite(n(p.lng)); }
  function placeId(p) { return s(p && (p.id || p._id || p.place_id || p.gmaps_url || "")); }

  // Haversine
  function haversineMeters(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }
  function distTo(p, q) { return haversineMeters(n(p.lat), n(p.lng), n(q.lat), n(q.lng)); }
  function pathMetersInOrder(points) {
    if (!points || points.length < 2) return 0;
    let sum = 0;
    for (let i = 1; i < points.length; i++) {
      sum += haversineMeters(n(points[i - 1].lat), n(points[i - 1].lng), n(points[i].lat), n(points[i].lng));
    }
    return sum;
  }
  function maxLegMetersInOrder(points) {
    if (!points || points.length < 2) return 0;
    let mx = 0;
    for (let i = 1; i < points.length; i++) {
      const d = haversineMeters(n(points[i - 1].lat), n(points[i - 1].lng), n(points[i].lat), n(points[i].lng));
      if (d > mx) mx = d;
    }
    return mx;
  }
  function centroid(points) {
    let lat = 0, lng = 0;
    for (const p of points) { lat += n(p.lat); lng += n(p.lng); }
    return { lat: lat / points.length, lng: lng / points.length };
  }

  // NN order
  function orderNearestNeighbor(points, startIdx) {
    if (!points || points.length < 3) return (points || []).slice();
    const pts = points.slice();
    const used = new Array(pts.length).fill(false);
    let cur = (startIdx >= 0 && startIdx < pts.length) ? startIdx : 0;
    const out = [pts[cur]];
    used[cur] = true;
    for (let step = 1; step < pts.length; step++) {
      let best = -1, bestD = Infinity;
      for (let i = 0; i < pts.length; i++) {
        if (used[i]) continue;
        const d = distTo(pts[cur], pts[i]);
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best === -1) break;
      used[best] = true;
      out.push(pts[best]);
      cur = best;
    }
    return out;
  }

  // Category normalize (use your existing normalizer if present)
  function normalizeCatKey(place) {
    const raw = place && (place._catKey || place.catKey || place.category || place.type || place.type_raw);
    if (typeof window.MAPSBYEMA_normalizeCategory === "function") {
      return window.MAPSBYEMA_normalizeCategory(raw);
    }
    return s(raw).trim().toLowerCase();
  }

  // Labels
  function normLabel(x) { return String(x || "").trim().toLowerCase().replace(/\s+/g, " "); }
  function getAllLabels(place) {
    const out = [];
    if (!place) return out;
    if (place.category) out.push(place.category);
    if (place.type) out.push(place.type);
    if (place.type_raw) out.push(place.type_raw);
    if (Array.isArray(place.categories)) out.push(...place.categories);
    if (Array.isArray(place.tags)) out.push(...place.tags);
    if (place.name) out.push(place.name);
    return out.map(normLabel).filter(Boolean);
  }

  // Parking filter (hard)
  const PARKING_WORDS = ["parking", "car park", "carpark", "parking lot", "parking garage", "parkhaus", "garage"];
  function isParking(place) {
    const cat = normLabel(normalizeCatKey(place));
    if (cat.includes("parking")) return true;
    const labels = getAllLabels(place);
    for (const l of labels) {
      if (l.includes("parking") || l.includes("car park") || l.includes("parkhaus")) return true;
    }
    // mild name-only heuristics
    const nm = normLabel(place && place.name);
    if (nm && PARKING_WORDS.some(w => nm.includes(w))) return true;
    return false;
  }

  // Food/drink (keep your existing label sets if you like; these are broad)
  const FOOD_LABELS = new Set([
    "places to eat", "restaurants", "restaurants & cafés", "restaurants & cafes",
    "restaurant & cafés", "restaurant & cafes", "restaurants and cafés", "restaurants and cafes",
    "coffee & desserts", "coffee and desserts", "brunch", "michelin restaurants",
    "markets", "lithuanian cuisine"
  ]);
  const DRINK_LABELS = new Set(["bars & clubs", "bars and clubs", "nightlife", "beach clubs", "+18"]);
  function isFood(place) {
    const labels = getAllLabels(place);
    return labels.some(l => FOOD_LABELS.has(l));
  }
  function isDrink(place) {
    const labels = getAllLabels(place);
    return labels.some(l => DRINK_LABELS.has(l));
  }

  // Scoring / quality filter (soft; never blocks fallback)
  function placeScore(p) {
    const r = n(p && p.rating);
    const rv = n(p && p.reviews);
    return (r * 10) + Math.log10(rv + 1) * 6;
  }
  function isLowSignal(p, relax) {
    const r = n(p && p.rating);
    const rv = n(p && p.reviews);
    if (relax) return (rv < 5 && r < 4.0);
    return (rv < 15 && r < 4.2);
  }

  // ---------- summary (neutral, works for walk/drive) ----------
  function stripStopNames(text, stops) {
    let out = String(text || "");
    if (!out) return out;
    const names = (stops || []).map(p => String(p && p.name || "")).filter(Boolean);
    names.sort((a, b) => b.length - a.length);
    for (const nm of names) {
      if (nm.length < 4) continue;
      const re = new RegExp(nm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
      out = out.replace(re, "");
    }
    out = out.replace(/\s{2,}/g, " ").replace(/\s+\./g, ".").trim();
    return out;
  }
  function cleanSentence(t) {
    t = s(t).trim();
    if (!t) return "";
    t = t.replace(/["“”]/g, "");
    if (t.length > 180) t = t.slice(0, 180);
    const low = t.toLowerCase();
    if (low.includes("we ") || low.includes(" i ") || low.includes("staff") || low.includes("service") || low.includes("ordered")) return "";
    if (!/[.!?]$/.test(t)) t += ".";
    return t;
  }

  const SUMMARY_OPENERS = [
    "A well-paced day plan with a smooth flow between standout stops.",
    "A balanced route designed to feel simple, logical, and easy to follow.",
    "A curated day that keeps transitions sensible and the experience cohesive.",
    "A flexible itinerary that mixes highlights with room to slow down.",
    "A clean, no-fuss plan built around a clear route and a relaxed rhythm.",
    "A cohesive day route that stays focused on one area at a time.",
    "A practical plan that avoids random detours and keeps the day flowing.",
    "A thoughtfully arranged day with natural breaks and easy transitions."
  ];
  const SUMMARY_CLOSERS = [
    "Expect a steady rhythm, minimal backtracking, and plenty of chances to pause.",
    "The order is designed to feel natural, with sensible transitions throughout.",
    "It stays focused and avoids filler—just a smooth sequence of solid stops.",
    "You’ll spend more time enjoying each spot and less time figuring out what’s next.",
    "The route keeps things tidy and connected, without unnecessary zigzags.",
    "It’s planned to feel curated rather than random, with a clear direction."
  ];
  function buildSummary(nonFoodStops) {
    const descs = nonFoodStops.map(p => cleanSentence(p.short_description || "")).filter(Boolean);
    const uniq = [];
    const seen = new Set();
    for (const d of descs) {
      const key = d.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      uniq.push(d);
      if (uniq.length >= 3) break;
    }
    let sum = [pick(SUMMARY_OPENERS)].concat(uniq).concat([pick(SUMMARY_CLOSERS)]).slice(0, 4).join(" ");
    sum = stripStopNames(sum, nonFoodStops);
    return sum;
  }

  // ---------- stop counts ----------
  function buildStopCountPlan(totalPlans) {
    if (totalPlans >= 25) return [5,5,5,5,5, 6,6,6,6,6, 7,7,7,7,7, 8,8,8,8,8, 9,9,9,9,9];
    if (totalPlans >= 15) return [5,5,5, 6,6,6, 7,7,7, 8,8,8, 9,9,9];
    const base = [5,6,7,8,9], out = [];
    for (let i = 0; i < totalPlans; i++) out.push(base[i % base.length]);
    return out;
  }

  // ---------- Upgrade #1: DBSCAN-ish clustering ----------
  function datasetSpreadKm(list) {
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const p of list) {
      const lat = n(p.lat), lng = n(p.lng);
      if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
    }
    const h = haversineMeters(minLat, minLng, maxLat, minLng) / 1000;
    const w = haversineMeters(minLat, minLng, minLat, maxLng) / 1000;
    return Math.max(h, w);
  }

  // Very small DBSCAN for up to a few thousand points (fine for your datasets)
  function dbscan(points, epsM, minPts) {
    const N = points.length;
    const labels = new Array(N).fill(0); // 0=unvisited, -1=noise, >0 clusterId
    let clusterId = 0;

    function neighbors(i) {
      const out = [];
      const pi = points[i];
      for (let j = 0; j < N; j++) {
        if (i === j) continue;
        const pj = points[j];
        if (distTo(pi, pj) <= epsM) out.push(j);
      }
      return out;
    }

    for (let i = 0; i < N; i++) {
      if (labels[i] !== 0) continue;
      const neigh = neighbors(i);
      if (neigh.length + 1 < minPts) { labels[i] = -1; continue; }
      clusterId++;
      labels[i] = clusterId;

      const queue = neigh.slice();
      while (queue.length) {
        const j = queue.shift();
        if (labels[j] === -1) labels[j] = clusterId;
        if (labels[j] !== 0) continue;
        labels[j] = clusterId;
        const neigh2 = neighbors(j);
        if (neigh2.length + 1 >= minPts) {
          for (const k of neigh2) if (!queue.includes(k)) queue.push(k);
        }
      }
    }

    const clusters = {};
    for (let i = 0; i < N; i++) {
      const id = labels[i];
      if (id > 0) (clusters[id] ||= []).push(points[i]);
    }
    const noise = [];
    for (let i = 0; i < N; i++) if (labels[i] === -1) noise.push(points[i]);
    return { clusters: Object.values(clusters), noise };
  }

  // ---------- Upgrade #3: smarter theme/title generator ----------
  // Map category patterns to human terms
  const THEME_WORDS = [
    { match: ["old", "must", "church", "relig", "cathedral", "monument"], vibe: ["Old Town", "Heritage", "Classic City"], verbs: ["stroll", "wander", "explore"], nouns: ["stories", "corners", "highlights"] },
    { match: ["museum", "history", "castle", "architecture", "gallery"], vibe: ["Museums", "History", "Architecture"], verbs: ["discover", "trace", "dive into"], nouns: ["heritage", "icons", "design"] },
    { match: ["view", "photo", "observ", "lookout", "panorama"], vibe: ["Views", "Photo Spots", "Scenic"], verbs: ["chase", "catch", "frame"], nouns: ["views", "angles", "moments"] },
    { match: ["park", "nature", "trail", "garden"], vibe: ["Nature", "Parks", "Green"], verbs: ["slow down in", "reset in", "breathe in"], nouns: ["green escapes", "calm paths", "open space"] },
    { match: ["beach", "sea", "coast", "shore"], vibe: ["Coast", "Sea", "Beach"], verbs: ["follow", "soak up", "ride along"], nouns: ["the coast", "sea air", "shoreline"] },
    { match: ["night", "bar", "club"], vibe: ["Night", "City Vibe", "After Dark"], verbs: ["ease into", "kick off", "wrap up with"], nouns: ["evening vibes", "city lights", "late bites"] },
    { match: ["market", "food", "restaurant", "cafe", "coffee"], vibe: ["Food", "Cafés", "Local Flavors"], verbs: ["taste", "sample", "pause for"], nouns: ["local flavors", "cozy cafés", "good bites"] }
  ];

  function inferThemeWords(stops) {
    const counts = {};
    for (const p of stops) {
      const ck = normalizeCatKey(p);
      if (!ck) continue;
      counts[ck] = (counts[ck] || 0) + 1;
    }
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(x => x[0]);
    const topStr = top.join(" ").toLowerCase();

    let best = null, bestHits = -1;
    for (const t of THEME_WORDS) {
      let hits = 0;
      for (const m of t.match) if (topStr.includes(m)) hits++;
      if (hits > bestHits) { bestHits = hits; best = t; }
    }
    return best || THEME_WORDS[0];
  }

  function makePlanTitle(stops, transportMode) {
    const t = inferThemeWords(stops);
    const vibe = pick(t.vibe);
    const verb = pick(t.verbs);
    const noun = pick(t.nouns);

    const formats = [
      `${vibe} ${transportMode === "drive" ? "Route" : "Loop"}: ${noun}`,
      `${verb.charAt(0).toUpperCase() + verb.slice(1)} ${noun}`,
      `${vibe} & ${noun}`,
      `${noun.charAt(0).toUpperCase() + noun.slice(1)} — ${vibe}`
    ];
    // reduce repetition by adding a small suffix sometimes
    const suffixes = ["", "", "", " (Easy Pace)", " (Highlights)", " (No Detours)"];
    return pick(formats) + pick(suffixes);
  }

  function makePlanTags(stops, transportMode) {
    const t = inferThemeWords(stops);
    const base = new Set([].concat(t.vibe).concat([transportMode === "drive" ? "Drive" : "Walk"]));
    const extras = ["Scenic", "Culture", "Relaxed", "Local", "Easy flow", "Photo spots", "No detours"];
    while (base.size < 5) base.add(pick(extras));
    return Array.from(base).slice(0, 5);
  }

  // ---------- Upgrade #2: optional Mapbox route validation ----------
  // We do NOT block rendering; we validate and cache for next load.
  function getMapboxToken_() {
    return window.MAPBOX_ACCESS_TOKEN || window.mapbox_access_token || window.MAPSBYEMA_MAPBOX_TOKEN || "";
  }
  function canValidateWithMapbox_() {
    const tok = getMapboxToken_();
    return !!(tok && typeof fetch === "function");
  }

  function mapboxProfileForMode(mode) {
    if (mode === "drive" || mode === "driving") return "driving";
    if (mode === "bike" || mode === "cycling") return "cycling";
    return "walking";
  }

  function buildDirectionsUrl(profile, coordsList, token) {
    const coords = coordsList.map(c => `${c.lng},${c.lat}`).join(";");
    const base = "https://api.mapbox.com/directions/v5/mapbox/";
    return `${base}${profile}/${coords}?geometries=geojson&overview=full&steps=false&access_token=${encodeURIComponent(token)}`;
  }

  async function validatePlansWithMapbox_(dest, ds, placesById) {
    const keyAtStart = mbeDsKey_(ds);
    if (window.__MBE_AUTOGUIDE_ACTIVE_KEY__ !== keyAtStart) return;

    try {
      if (!dest || !Array.isArray(dest.plans) || !dest.plans.length) return;
      if (!canValidateWithMapbox_()) return;

      const token = getMapboxToken_();
      const routeCache = mbeLoadRouteCache_(ds) || { v: 1, plans: {} };

      // Validate only a few per run to avoid rate limits
      const MAX_VALIDATE = 6;
      let validated = 0;

      for (const plan of dest.plans) {
        if (validated >= MAX_VALIDATE) break;
        if (!plan || !plan.id || !Array.isArray(plan.stops) || plan.stops.length < 2) continue;

        // if already cached, apply quickly
        if (routeCache.plans[plan.id]) {
          const c = routeCache.plans[plan.id];
          if (c.transport_mode) plan.transport_mode = c.transport_mode;
          if (c.route_distance_m) plan.route_distance_m = c.route_distance_m;
          if (c.route_duration_s) plan.route_duration_s = c.route_duration_s;
          continue;
        }

        const coordsList = [];
        for (const sid of plan.stops) {
          const p = placesById[sid];
          if (!p || !hasCoords(p)) continue;
          coordsList.push({ lat: n(p.lat), lng: n(p.lng) });
          if (coordsList.length >= 12) break; // keep URL reasonable (enough for validation)
        }
        if (coordsList.length < 2) continue;

        // try walking and driving, pick best realistic one
        const profWalk = "walking";
        const profDrive = "driving";

        const urlW = buildDirectionsUrl(profWalk, coordsList, token);
        const urlD = buildDirectionsUrl(profDrive, coordsList, token);

        const [rw, rd] = await Promise.all([
          fetch(urlW).then(r => r.ok ? r.json() : null).catch(() => null),
          fetch(urlD).then(r => r.ok ? r.json() : null).catch(() => null)
        ]);

        function pickRoute(json) {
          const r = json && json.routes && json.routes[0];
          if (!r) return null;
          return { distance: n(r.distance), duration: n(r.duration) };
        }
        const W = pickRoute(rw);
        const D = pickRoute(rd);
        if (!W && !D) continue;

        // Decide mode:
        // - if walking is wildly longer than straight-line (mountains / no footpaths) -> drive
        // - if walking duration is reasonable and legs are short -> walk
        const straight = haversineMeters(coordsList[0].lat, coordsList[0].lng, coordsList[coordsList.length - 1].lat, coordsList[coordsList.length - 1].lng);
        const walkRatio = (W && straight > 50) ? (W.distance / straight) : 1.0;

        let mode = plan.transport_mode || "walk";
        // default to drive if drive exists and walk is “bad”
        if (D && (!W || walkRatio > 2.6)) mode = "drive";
        // default to walk if walk exists and not insane
        if (W && walkRatio <= 2.6) mode = "walk";

        const chosen = (mode === "walk") ? (W || D) : (D || W);
        if (!chosen) continue;

        plan.transport_mode = mode;
        plan.route_distance_m = chosen.distance;
        plan.route_duration_s = chosen.duration;

        routeCache.plans[plan.id] = {
          transport_mode: mode,
          route_distance_m: chosen.distance,
          route_duration_s: chosen.duration
        };

        validated++;
      }

      if (window.__MBE_AUTOGUIDE_ACTIVE_KEY__ !== keyAtStart) return;
      mbeSaveRouteCache_(ds, routeCache);

      if (window.__MBE_AUTOGUIDE_ACTIVE_KEY__ !== keyAtStart) return;
      mbeSaveCache_(ds, dest);

      // Save route cache and destination cache (so next load is better)
      mbeSaveRouteCache_(ds, routeCache);
      mbeSaveCache_(ds, dest);
    } catch (e) {
      // ignore validation errors
    }
  }

  // ---------- generator core ----------
  function generateDayPlansFromPlaces(places, opts, ds) {
    opts = opts || {};
    const maxRepeatBase = n(opts.maxRepeat || 3);
    const minKmBase = n(opts.minKm || 2);
    const maxKmBase = n(opts.maxKm || 10);

    const all = (Array.isArray(places) ? places : [])
      .filter(hasCoords)
      .filter(p => !isParking(p));

    if (all.length < 20) return { plans: [], placesById: {} };

    const placesById = {};
    for (const p of all) {
      const id = placeId(p);
      if (id) placesById[id] = p;
    }

    const food = all.filter(p => isFood(p));
    const drinks = all.filter(p => isDrink(p));
    const nonFood = all.filter(p => !isFood(p));

    const planCount = (all.length >= 200) ? 25 : 15;
    const stopCounts = buildStopCountPlan(planCount);

    // decide dataset “scale”
    const spreadKm = datasetSpreadKm(all);
    const datasetLikelyDrive = spreadKm > 35; // city vs region heuristic

    // clustering parameters (eps depends on scale)
    const epsM = datasetLikelyDrive ? 12000 : 2200;     // ~12km for region, ~2.2km for city
    const minPts = datasetLikelyDrive ? 4 : 8;
    const clusterResult = dbscan(nonFood.slice(0, 900), epsM, minPts); // cap points for speed
    let clusters = clusterResult.clusters || [];
    const noise = clusterResult.noise || [];

    // ensure we always have some clusters
    if (!clusters.length) clusters = [nonFood.slice(0, 250)];

    // order clusters by “density+quality”
    clusters = clusters
      .map(c => {
        const q = c.slice().sort((a, b) => placeScore(b) - placeScore(a)).slice(0, 8);
        const score = q.reduce((acc, p) => acc + placeScore(p), 0) / Math.max(1, q.length);
        return { c, score, size: c.length };
      })
      .sort((a, b) => (b.size + b.score) - (a.size + a.score))
      .map(x => x.c);

    const usedCount = {};
    function canUse(p, maxRepeat) {
      const id = placeId(p);
      if (!id) return false;
      return (usedCount[id] || 0) < maxRepeat;
    }
    function markUse(p) {
      const id = placeId(p);
      if (!id) return;
      usedCount[id] = (usedCount[id] || 0) + 1;
    }

    // category cap (soft guard)
    function catCap(relax) { return relax ? 3 : 2; }

    // detour ratio
    function detourRatio(points) {
      if (!points || points.length < 2) return 1;
      const straight = distTo(points[0], points[points.length - 1]);
      if (straight < 200) return 1;
      const path = pathMetersInOrder(points);
      return path / straight;
    }

    function modeFromGeometry(points, fallback) {
      const km = pathMetersInOrder(points) / 1000;
      const avgLegKm = km / Math.max(1, (points.length - 1));
      // if average hop is big -> drive
      if (avgLegKm > 2.0) return "drive";
      // if dataset is wide and hops medium -> drive
      if (fallback === "drive" && avgLegKm > 1.2) return "drive";
      return "walk";
    }

    function pickFoodNearPoint(pt, maxRepeat, relax) {
      const radius = datasetLikelyDrive ? (relax ? 12000 : 8000) : (relax ? 3500 : 2500);
      let best = null, bestD = Infinity;
      const list = food.length ? food : drinks;
      for (const f of list) {
        if (!f || isParking(f)) continue;
        if (!canUse(f, maxRepeat)) continue;
        if (!relax && isLowSignal(f, false)) continue;
        const d = distTo(pt, f);
        if (d <= radius && d < bestD) { best = f; bestD = d; }
      }
      return best;
    }

    function buildPlanFromCluster(cluster, idx, desiredStops, relax) {
      const maxRepeat = relax ? (maxRepeatBase + 1) : maxRepeatBase;
      const cap = catCap(relax);

      // desired food count
      const foodN = (desiredStops >= 8 && food.length >= 2) ? 2 : 1;
      const needNonFood = Math.max(3, desiredStops - foodN);

      // filter usable
      let pool = cluster.filter(p => canUse(p, maxRepeat) && !isParking(p));
      if (!relax) pool = pool.filter(p => !isLowSignal(p, false));
      if (pool.length < needNonFood) {
        // allow low-signal if needed
        pool = cluster.filter(p => canUse(p, maxRepeat) && !isParking(p));
      }
      if (pool.length < needNonFood) return null;

      // HUB = best score in pool
      pool = pool.slice().sort((a, b) => placeScore(b) - placeScore(a));
      const hub = pool[0];

      // Tiers (anchors / main / gems)
      const hiCut = Math.max(2, Math.floor(pool.length * 0.15));
      const midCut = Math.max(hiCut + 3, Math.floor(pool.length * 0.70));
      const high = pool.slice(0, hiCut);
      const mid = pool.slice(hiCut, midCut);
      const low = pool.slice(midCut);

      const picks = [];
      const pickedSet = new Set();
      const catCount = {};

      function addPick(p) {
        const id = placeId(p);
        if (!id || pickedSet.has(id)) return false;
        if (!canUse(p, maxRepeat)) return false;
        const ck = normalizeCatKey(p) || "unknown";
        if ((catCount[ck] || 0) >= cap) return false;
        picks.push(p);
        pickedSet.add(id);
        catCount[ck] = (catCount[ck] || 0) + 1;
        return true;
      }

      // anchors
      addPick(high[0]);
      if (needNonFood >= 6 && high[1]) addPick(high[1]);

      // fill: mostly mid, sometimes a gem
      while (picks.length < needNonFood) {
        const last = picks[picks.length - 1] || hub;
        const useGem = (__rand() < 0.22);
        const src = (useGem ? low : mid);
        let best = null, bestD = Infinity;

        for (const cand of src) {
          const id = placeId(cand);
          if (!id || pickedSet.has(id)) continue;
          if (!canUse(cand, maxRepeat)) continue;
          const ck = normalizeCatKey(cand) || "unknown";
          if ((catCount[ck] || 0) >= cap) continue;

          const d = distTo(last, cand);
          if (d < bestD) { bestD = d; best = cand; }
        }

        // fallback to any pool
        if (!best) {
          for (const cand of pool) {
            const id = placeId(cand);
            if (!id || pickedSet.has(id)) continue;
            if (!canUse(cand, maxRepeat)) continue;
            const ck = normalizeCatKey(cand) || "unknown";
            if ((catCount[ck] || 0) >= cap) continue;
            const d = distTo(last, cand);
            if (d < bestD) { bestD = d; best = cand; }
          }
        }
        if (!best) break;
        addPick(best);
      }

      if (picks.length < needNonFood) return null;

      // order (NN from best start-ish candidate)
      let startIdx = 0;
      // bias start from hub or best-scored
      startIdx = picks.findIndex(p => placeId(p) === placeId(hub));
      if (startIdx < 0) startIdx = 0;

      let ordered = orderNearestNeighbor(picks, startIdx);

      // anti zigzag (soft)
      const det = detourRatio(ordered);
      const detCap = datasetLikelyDrive ? (relax ? 3.3 : 2.9) : (relax ? 2.8 : 2.4);
      if (det > detCap && !relax) {
        // try alternative start
        ordered = orderNearestNeighbor(picks, Math.floor(__rand() * picks.length));
      }

      // pick transport mode from geometry (pre-validation)
      const fallbackMode = datasetLikelyDrive ? "drive" : "walk";
      let transportMode = modeFromGeometry(ordered, fallbackMode);

      // add food by day percent (neutral)
      const finalStops = ordered.slice();
      const midIdx = Math.floor(finalStops.length * 0.50);
      const lateIdx = Math.floor(finalStops.length * 0.80);

      const f1 = pickFoodNearPoint(finalStops[midIdx] || finalStops[finalStops.length - 1], maxRepeat, relax);
      if (f1) finalStops.splice(midIdx, 0, f1);

      if (foodN === 2) {
        const f2 = pickFoodNearPoint(finalStops[lateIdx] || finalStops[finalStops.length - 1], maxRepeat, relax);
        if (f2 && placeId(f2) !== placeId(f1)) finalStops.splice(lateIdx, 0, f2);
      }

      // fit to desired
      while (finalStops.length > desiredStops) {
        const mid = Math.floor(finalStops.length / 2);
        let rm = mid;
        for (let step = 0; step < finalStops.length; step++) {
          const i = (mid + step) % finalStops.length;
          if (!isFood(finalStops[i])) { rm = i; break; }
        }
        finalStops.splice(rm, 1);
      }

      // if still short, add nearest from pool (soft)
      while (finalStops.length < desiredStops) {
        const anchor = finalStops[finalStops.length - 1];
        let best = null, bestD = Infinity;
        for (const cand of pool) {
          const id = placeId(cand);
          if (!id) continue;
          if (finalStops.some(x => placeId(x) === id)) continue;
          if (!canUse(cand, maxRepeat)) continue;
          const d = distTo(anchor, cand);
          if (d < bestD) { bestD = d; best = cand; }
        }
        if (!best) break;
        finalStops.splice(finalStops.length - 1, 0, best);
      }

      // distance guard is soft: we allow, then transport_mode can become drive
      const km = pathMetersInOrder(finalStops) / 1000;
      const maxLegM = maxLegMetersInOrder(finalStops);

      // if legs are too big for walk, switch to drive rather than rejecting
      if (transportMode === "walk") {
        const legCap = relax ? 1800 : 1200;
        if (maxLegM > legCap || km > (relax ? 14 : 11)) transportMode = "drive";
      }

      // mark usage
      for (const p of finalStops) markUse(p);

      // theme/title/tags
      const title = makePlanTitle(ordered, transportMode);
      const tags = makePlanTags(ordered, transportMode);

      return {
        id: "autogen_" + slugify(title) + "_" + (idx + 1),
        title,
        duration: "1 day",
        tags,
        summary: buildSummary(ordered),
        stops: finalStops.map(p => placeId(p)).filter(Boolean),
        transport_mode: transportMode,
        // optional meta for debugging:
        meta: { km_est: Math.round(km * 10) / 10, spread_km: Math.round(spreadKm), detour: Math.round(detourRatio(ordered) * 100) / 100 }
      };
    }

    // ---------- plan building loop ----------
    const plans = [];
    const clusterQueue = shuffle(clusters.slice());

    // Primary pass: use clusters in order
    for (let i = 0; i < stopCounts.length && plans.length < planCount; i++) {
      const desired = Math.max(5, Math.min(10, stopCounts[i]));
      let plan = null;

      // try a few clusters to avoid dead ends
      for (let t = 0; t < Math.min(8, clusterQueue.length); t++) {
        const c = clusterQueue[(i + t) % clusterQueue.length];
        plan = buildPlanFromCluster(c, plans.length, desired, false);
        if (plan) break;
      }

      if (!plan) {
        // fallback: build from best cluster + allow relax
        const c = clusterQueue[0] || nonFood;
        plan = buildPlanFromCluster(c, plans.length, desired, true);
      }

      // last fallback: from global pool
      if (!plan) {
        const global = nonFood.slice().sort((a, b) => placeScore(b) - placeScore(a)).slice(0, 220);
        plan = buildPlanFromCluster(global, plans.length, desired, true);
      }

      if (plan) plans.push(plan);
    }

    // Relax fill: ensure full count
    let guard = 0;
    while (plans.length < planCount && guard++ < 200) {
      const desired = pick([5,6,7,8,9]);
      const c = pick(clusterQueue) || nonFood;
      let plan = buildPlanFromCluster(c, plans.length, desired, true);
      if (!plan) {
        const global = nonFood.slice().sort((a, b) => placeScore(b) - placeScore(a)).slice(0, 260);
        plan = buildPlanFromCluster(global, plans.length, desired, true);
      }
      if (plan) plans.push(plan);
      else break;
    }

    return { plans: plans.slice(0, planCount), placesById };
  }

  // ---------- PUBLIC API ----------
  window.MBE_buildAutoGuideDestination = function (places, ds) {
    const activeKey = mbeDsKey_(ds);
    window.__MBE_AUTOGUIDE_ACTIVE_KEY__ = activeKey;

    // 1) cache
    const cached = mbeLoadCache_(ds);
    if (cached) {
      // If we have cached plans but missing route validations, we can still validate async
      // (does not block render)
      try {
        const placesById = {};
        for (const p of (Array.isArray(places) ? places : [])) {
          const id = placeId(p);
          if (id) placesById[id] = p;
        }
        setTimeout(() => validatePlansWithMapbox_(cached, ds, placesById), 0);
      } catch (e) {}
      return cached;
    }

    // 2) seed PRNG
    __rand = mulberry32(mbeHash32_(mbeDsKey_(ds)));

    const list = Array.isArray(places) ? places : [];
    const gen = generateDayPlansFromPlaces(list, { maxRepeat: 3, minKm: 2, maxKm: 10 }, ds);

    const filename = s(ds && (ds.filename || ds.url || "")).split("|")[0].replace(/\.json$/i, "");
    const base = filename.replace(/-places$/i, "");
    const destName =
      (ds && ds.title) ||
      (function () {
        const parts = base.split("-").filter(Boolean);
        const guess = parts[1] && !/^\d+$/.test(parts[1]) ? parts[1] : (parts[0] || "Travel");
        return guess.charAt(0).toUpperCase() + guess.slice(1);
      })();

    const dest = {
      version: 1,
      destination_slug: slugify(base || destName),
      destination_name: destName,
      updated_at: new Date().toISOString().slice(0, 10),
      plans: gen.plans
    };

    // save base plans immediately
    mbeSaveCache_(ds, dest);

    // 3) async validate via Mapbox (cached for next loads)
    // NOTE: this does not block rendering and should not break anything.
    try { setTimeout(() => validatePlansWithMapbox_(dest, ds, gen.placesById), 0); } catch (e) {}

    return dest;
  };
})();